const {applyLotCardSnapshot} = require('./lot-product-snapshot');

const asDate = value => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getCatalogueClosingDate = catalogue => [
    catalogue?.effectiveFinalLotEndDate,
    catalogue?.scheduledFinalLotEndDate,
    catalogue?.endDate
].find(value => asDate(value)) || null;

const normaliseState = catalogue => String(catalogue?.state || '').trim().toLowerCase();

const normaliseCatalogueNumber = value => {
    if (typeof value !== 'string') return null;
    const catalogueNumber = value.trim();
    return catalogueNumber && catalogueNumber.length <= 128 ? catalogueNumber : null;
};

const DEFAULT_AUCTION_CATALOGUE_PATH = '/auctions';

const getCataloguePublicCopy = catalogue => {
    const isTender = String(catalogue?.saleMethod || '').trim().toLowerCase() === 'tender';

    return isTender
        ? {
            eventName: 'tender',
            eventNameTitle: 'Tender',
            actionInstruction: 'submit an offer',
            lotActionInstruction: 'submit your offer',
            scheduleStartsLabel: 'Offers Open',
            scheduleClosesLabel: 'Offers Close'
        }
        : {
            eventName: 'auction',
            eventNameTitle: 'Auction',
            actionInstruction: 'bid',
            lotActionInstruction: 'place your bid',
            scheduleStartsLabel: 'Bidding Starts',
            scheduleClosesLabel: 'Bidding Closes'
        };
};

const getCatalogueCollectionPublicCopy = (catalogues = [], {past = false, scope = null} = {}) => {
    const methods = new Set((Array.isArray(catalogues) ? catalogues : [])
        .filter(Boolean)
        .map(catalogue => getCataloguePublicCopy(catalogue).eventNameTitle));
    const tenderOnly = methods.size === 1 && methods.has('Tender');
    const mixed = methods.size > 1;

    if (scope === 'live') {
        if (mixed) {
            return {
                title: 'Live Auctions & Tenders',
                description: 'Browse auctions open for bidding and tenders open for offers.'
            };
        }

        if (tenderOnly) {
            return {
                title: 'Live Tenders',
                description: 'Tenders currently open for offers.'
            };
        }

        return {
            title: 'Live Auctions',
            description: 'Auctions currently open for bidding.'
        };
    }

    if (scope === 'upcoming') {
        if (mixed) {
            return {
                title: 'Upcoming Auctions & Tenders',
                description: 'Browse auctions and tenders scheduled to open.'
            };
        }

        if (tenderOnly) {
            return {
                title: 'Upcoming Tenders',
                description: 'Tenders scheduled to open for offers.'
            };
        }

        return {
            title: 'Upcoming Auctions',
            description: 'Auctions scheduled to open for bidding.'
        };
    }

    if (methods.size === 0) {
        return {
            title: past ? 'Past Auctions' : 'Auctions',
            description: past
                ? 'Browse past auction catalogues.'
                : 'Browse auctions that are open for bidding or coming soon.'
        };
    }

    if (mixed) {
        return {
            title: past ? 'Past Auctions & Tenders' : 'Auctions & Tenders',
            description: past
                ? 'Browse past auction and tender catalogues.'
                : 'Browse auctions and tenders that are open or coming soon.'
        };
    }

    if (tenderOnly) {
        return {
            title: past ? 'Past Tenders' : 'Tenders',
            description: past
                ? 'Browse past tender catalogues.'
                : 'Browse tenders that are open for offers or coming soon.'
        };
    }

    return {
        title: past ? 'Past Auctions' : 'Auctions',
        description: past
            ? 'Browse past auction catalogues.'
            : 'Browse auctions that are open for bidding or coming soon.'
    };
};

const auctionCatalogueDateFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
};

const formatAuctionCatalogueDate = (value, locales = 'en-AU') => {
    const date = asDate(value);
    if (!date) return 'To Be Confirmed';

    return new Intl.DateTimeFormat(locales, auctionCatalogueDateFormatOptions).format(date);
};

const formatAuctionCatalogueDateRange = (startValue, endValue, locales = 'en-AU') => {
    const startDate = asDate(startValue);
    const endDate = asDate(endValue);
    if (!startDate || !endDate) return 'To Be Confirmed';

    const formatter = new Intl.DateTimeFormat(locales, auctionCatalogueDateFormatOptions);
    return typeof formatter.formatRange === 'function'
        ? formatter.formatRange(startDate, endDate)
        : `${formatter.format(startDate)} – ${formatter.format(endDate)}`;
};

const buildAuctionCatalogueDateMarkup = value => {
    const date = asDate(value);
    if (!date) return 'To Be Confirmed';

    return `<span data-auction-catalogue-date="${date.getTime()}">${formatAuctionCatalogueDate(date)}</span>`;
};

const buildAuctionCatalogueDateRangeMarkup = (startValue, endValue) => {
    const startDate = asDate(startValue);
    const endDate = asDate(endValue);
    if (!startDate || !endDate) return 'To Be Confirmed';

    return `<span data-auction-catalogue-date-range-start="${startDate.getTime()}" data-auction-catalogue-date-range-end="${endDate.getTime()}">${formatAuctionCatalogueDateRange(startDate, endDate)}</span>`;
};

const isStaggeredAuctionCatalogue = catalogue => {
    if (String(catalogue?.saleMethod || '').trim().toLowerCase() === 'tender') return false;

    const intervalSeconds = Number(catalogue?.lotCloseIntervalSeconds);
    const lotCount = Number(catalogue?.lotCount);
    const firstLotEndDate = asDate(catalogue?.firstLotEndDate || catalogue?.endDate);
    const finalLotEndDate = asDate(catalogue?.scheduledFinalLotEndDate);

    return intervalSeconds > 0
        && lotCount > 1
        && firstLotEndDate
        && finalLotEndDate
        && finalLotEndDate > firstLotEndDate;
};

const buildAuctionCatalogueHeaderText = catalogue => {
    if (!catalogue) return '';
    const copy = getCataloguePublicCopy(catalogue);
    const introduction = typeof catalogue.introduction === 'string'
        ? catalogue.introduction.trim()
        : '';
    const staggered = isStaggeredAuctionCatalogue(catalogue);
    const closesLabel = staggered ? 'Scheduled Lot Closings' : copy.scheduleClosesLabel;
    const closesValue = staggered
        ? buildAuctionCatalogueDateRangeMarkup(
            catalogue.firstLotEndDate || catalogue.endDate,
            catalogue.scheduledFinalLotEndDate
        )
        : buildAuctionCatalogueDateMarkup(catalogue.endDate);
    const schedule = '<div class="auction-catalogue-schedule">'
        + `<span class="auction-catalogue-schedule__item"><strong>${copy.scheduleStartsLabel}:</strong>${buildAuctionCatalogueDateMarkup(catalogue.startDate)}</span>`
        + '<span class="auction-catalogue-schedule__separator" aria-hidden="true">→</span>'
        + `<span class="auction-catalogue-schedule__item"><strong>${closesLabel}:</strong>${closesValue}</span>`
        + '</div>';

    return [introduction, schedule].filter(Boolean).join('');
};

const localiseAuctionCatalogueSchedule = root => {
    if (!root?.querySelectorAll) return;

    const formatter = new Intl.DateTimeFormat(undefined, auctionCatalogueDateFormatOptions);
    root.querySelectorAll('[data-auction-catalogue-date]').forEach(element => {
        const date = asDate(Number(element.getAttribute('data-auction-catalogue-date')));
        if (date) element.textContent = formatter.format(date);
    });
    root.querySelectorAll('[data-auction-catalogue-date-range-start]').forEach(element => {
        const startDate = asDate(Number(element.getAttribute('data-auction-catalogue-date-range-start')));
        const endDate = asDate(Number(element.getAttribute('data-auction-catalogue-date-range-end')));
        if (!startDate || !endDate) return;
        element.textContent = typeof formatter.formatRange === 'function'
            ? formatter.formatRange(startDate, endDate)
            : `${formatter.format(startDate)} – ${formatter.format(endDate)}`;
    });
};

const normaliseAuctionCataloguePath = value => {
    if (typeof value !== 'string' || !value.trim()) return DEFAULT_AUCTION_CATALOGUE_PATH;

    const pathname = value.trim()
        .split(/[?#]/, 1)[0]
        .replace(/\/{2,}/g, '/')
        .replace(/\/$/, '');
    const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;

    if (
        withLeadingSlash === '/'
        || withLeadingSlash.length > 128
        || !/^\/[A-Za-z0-9][A-Za-z0-9/_-]*$/.test(withLeadingSlash)
    ) return DEFAULT_AUCTION_CATALOGUE_PATH;

    return withLeadingSlash;
};

const buildAuctionCatalogueHref = (basePath, catalogueNumber = null) => {
    const base = normaliseAuctionCataloguePath(basePath);
    const number = normaliseCatalogueNumber(catalogueNumber);
    if (!number) return base;
    return `${base}/${encodeURIComponent(number)}`;
};

const matchAuctionCataloguePath = (pathname, basePath) => {
    const base = normaliseAuctionCataloguePath(basePath);
    const requestedPath = String(pathname || '').replace(/\/$/, '') || '/';
    if (requestedPath === base) return {mode: 'index'};
    if (requestedPath === `${base}/live`) return {mode: 'live'};
    if (requestedPath === `${base}/upcoming`) return {mode: 'upcoming'};
    if (requestedPath === `${base}/archive`) return {mode: 'archive'};
    if (!requestedPath.startsWith(`${base}/`)) return null;

    const segments = requestedPath.slice(base.length + 1).split('/').filter(Boolean);
    if (segments.length !== 1) return null;

    let catalogueNumber;
    try {
        catalogueNumber = normaliseCatalogueNumber(decodeURIComponent(segments[0]));
    } catch {
        return null;
    }
    if (!catalogueNumber) return null;
    return {
        mode: 'detail',
        catalogueNumber
    };
};

const LOT_STATUS_BY_VALUE = {
    1: 'Open',
    2: 'Sold',
    3: 'Withdrawn',
    4: 'PassedIn',
    5: 'SaleFellThrough'
};

const normaliseLotStatus = value => LOT_STATUS_BY_VALUE[value]
    || Object.values(LOT_STATUS_BY_VALUE)
        .find(status => status.toLowerCase() === String(value || '').trim().toLowerCase())
    || value;

const getProductId = (hit = {}) => hit?._source?.uuid || hit?._source?.productUuid || hit?._id;

const getCatalogueLotsByProductId = catalogue => new Map((catalogue?.lots || [])
    .filter(lot => String(normaliseLotStatus(lot?.status)).toLowerCase() !== 'withdrawn')
    .filter(lot => lot?.product?.id)
    .map(lot => [String(lot.product.id).toLowerCase(), lot]));

const buildCatalogueLotNumberSort = catalogue => {
    const lotOrder = {};

    (catalogue?.lots || [])
        .filter(lot => String(normaliseLotStatus(lot?.status)).toLowerCase() !== 'withdrawn')
        .filter(lot => lot?.product?.id && Number.isFinite(Number(lot?.lotNumber)))
        .forEach(lot => {
            lotOrder[String(lot.product.id)] = Number(lot.lotNumber);
        });

    return [{
        _script: {
            type: 'number',
            script: {
                lang: 'painless',
                inline: "if (params.lotOrder.containsKey(doc['_id'].value)) { return params.lotOrder[doc['_id'].value]; } return 2147483647;",
                params: {lotOrder}
            },
            order: 'asc'
        }
    }];
};

const getLotValue = (lot, key, fallback) => Object.prototype.hasOwnProperty.call(lot || {}, key)
    ? lot[key]
    : fallback;

const buildCatalogueSalesEvent = (existingSalesEvent, catalogue, lot) => {
    const sameCatalogue = String(existingSalesEvent?.catalogueNumber || '').toLowerCase()
        === String(catalogue?.catalogueNumber || '').toLowerCase();

    return {
        ...existingSalesEvent,
        title: catalogue?.title,
        catalogueNumber: catalogue?.catalogueNumber,
        lotNumber: lot?.lotNumber,
        lotStatus: normaliseLotStatus(lot?.status),
        startDate: catalogue?.startDate,
        scheduledEndDate: lot?.scheduledEndDate || catalogue?.endDate,
        endDate: lot?.effectiveEndDate || catalogue?.endDate,
        sellingMethod: lot?.sellingMethod || catalogue?.saleMethod || 'Auction',
        feeDescription: getLotValue(lot, 'feeDescription', sameCatalogue ? existingSalesEvent?.feeDescription : null),
        reserveMetLabel: getLotValue(lot, 'reserveMetLabel', sameCatalogue ? existingSalesEvent?.reserveMetLabel : null),
        leadingStatusLabel: getLotValue(lot, 'leadingStatusLabel', sameCatalogue ? existingSalesEvent?.leadingStatusLabel : null),
        outbidStatusLabel: getLotValue(lot, 'outbidStatusLabel', sameCatalogue ? existingSalesEvent?.outbidStatusLabel : null),
        leadingHelperText: getLotValue(lot, 'leadingHelperText', sameCatalogue ? existingSalesEvent?.leadingHelperText : null),
        outbidHelperText: getLotValue(lot, 'outbidHelperText', sameCatalogue ? existingSalesEvent?.outbidHelperText : null),
        highestBid: getLotValue(lot, 'highestBid', sameCatalogue ? existingSalesEvent?.highestBid : null),
        totalBids: getLotValue(lot, 'totalBids', sameCatalogue ? (existingSalesEvent?.totalBids || 0) : 0),
        reserveMet: getLotValue(lot, 'reserveMet', sameCatalogue ? !!existingSalesEvent?.reserveMet : false),
        lotId: lot?.id
    };
};

const decorateProductsWithCatalogue = (products, catalogue, {sortByLotNumber = false} = {}) => {
    if (!products || !Array.isArray(products.hits)) return products;
    const lotsByProductId = getCatalogueLotsByProductId(catalogue);

    const hits = products.hits.map(hit => {
        const lot = lotsByProductId.get(String(getProductId(hit) || '').toLowerCase());
        if (!lot) return hit;

        return {
            ...hit,
            _source: applyLotCardSnapshot({
                ...hit._source,
                catalogueContext: {
                    catalogueNumber: catalogue.catalogueNumber,
                    lotId: lot.id
                },
                salesEvent: buildCatalogueSalesEvent(hit?._source?.salesEvent, catalogue, lot)
            }, lot.cardSnapshot)
        };
    });

    if (sortByLotNumber) {
        hits.sort((left, right) => (left?._source?.salesEvent?.lotNumber ?? Number.MAX_SAFE_INTEGER)
            - (right?._source?.salesEvent?.lotNumber ?? Number.MAX_SAFE_INTEGER));
    }

    return {...products, hits};
};

const getCatalogueProductIds = catalogue => {
    const ids = (catalogue?.lots || [])
        .filter(lot => String(lot?.status).toLowerCase() !== 'withdrawn' && lot?.status !== 3)
        .map(lot => lot?.product?.id)
        .filter(Boolean)
        .map(String);

    return [...new Set(ids.length ? ids : ['__empty_catalogue__'])];
};

const isLiveCatalogue = (catalogue, now) => {
    const state = normaliseState(catalogue);
    if (state) return state === 'live';

    const startDate = asDate(catalogue?.startDate);
    const endDate = asDate(getCatalogueClosingDate(catalogue));
    return (!startDate || startDate <= now) && (!endDate || endDate > now);
};

const isScheduledCatalogue = (catalogue, now) => {
    const state = normaliseState(catalogue);
    if (state) return state === 'scheduled';

    const startDate = asDate(catalogue?.startDate);
    return !!startDate && startDate > now;
};

const getLiveCatalogues = (catalogues = [], nowValue = new Date()) => {
    const now = asDate(nowValue) || new Date();
    return (Array.isArray(catalogues) ? catalogues.filter(Boolean) : [])
        .filter(catalogue => isLiveCatalogue(catalogue, now))
        .sort((left, right) => (asDate(getCatalogueClosingDate(left))?.getTime() || Number.MAX_SAFE_INTEGER)
            - (asDate(getCatalogueClosingDate(right))?.getTime() || Number.MAX_SAFE_INTEGER));
};

const getUpcomingCatalogues = (catalogues = [], nowValue = new Date()) => {
    const now = asDate(nowValue) || new Date();
    return (Array.isArray(catalogues) ? catalogues.filter(Boolean) : [])
        .filter(catalogue => isScheduledCatalogue(catalogue, now))
        .sort((left, right) => (asDate(left.startDate)?.getTime() || Number.MAX_SAFE_INTEGER)
            - (asDate(right.startDate)?.getTime() || Number.MAX_SAFE_INTEGER));
};

const selectCurrentCatalogue = (catalogues = [], nowValue = new Date()) => {
    const live = getLiveCatalogues(catalogues, nowValue);
    if (live.length) return live[0];

    return getUpcomingCatalogues(catalogues, nowValue)[0] || null;
};

const getCurrentCatalogues = (catalogues = [], nowValue = new Date()) => {
    return [
        ...getLiveCatalogues(catalogues, nowValue),
        ...getUpcomingCatalogues(catalogues, nowValue)
    ];
};

const resolveLiveCatalogueShortcutHref = (
    catalogues = [],
    basePath = DEFAULT_AUCTION_CATALOGUE_PATH,
    nowValue = new Date()
) => {
    const base = normaliseAuctionCataloguePath(basePath);
    const live = getLiveCatalogues(catalogues, nowValue);

    return live.length === 1
        ? buildAuctionCatalogueHref(base, live[0].catalogueNumber)
        : base;
};

const resolveUpcomingCatalogueShortcutHref = (
    catalogues = [],
    basePath = DEFAULT_AUCTION_CATALOGUE_PATH,
    nowValue = new Date()
) => {
    const base = normaliseAuctionCataloguePath(basePath);
    const upcoming = getUpcomingCatalogues(catalogues, nowValue);

    return upcoming.length === 1
        ? buildAuctionCatalogueHref(base, upcoming[0].catalogueNumber)
        : base;
};

const getArchivedCatalogues = (catalogues = []) => (Array.isArray(catalogues) ? catalogues : [])
    .filter(catalogue => ['ended', 'finalised'].includes(normaliseState(catalogue)))
    .sort((left, right) => (asDate(getCatalogueClosingDate(right))?.getTime() || 0)
        - (asDate(getCatalogueClosingDate(left))?.getTime() || 0));

const shouldHideAuctionPageFromCrawlers = (mode, catalogue = null) => {
    if (mode === 'archive' || mode === 'not-found') return true;
    return ['ended', 'finalised', 'cancelled'].includes(normaliseState(catalogue));
};

module.exports = {
    DEFAULT_AUCTION_CATALOGUE_PATH,
    buildAuctionCatalogueHref,
    buildAuctionCatalogueHeaderText,
    buildCatalogueLotNumberSort,
    decorateProductsWithCatalogue,
    getCatalogueClosingDate,
    getCatalogueProductIds,
    getArchivedCatalogues,
    getCurrentCatalogues,
    getLiveCatalogues,
    getUpcomingCatalogues,
    formatAuctionCatalogueDate,
    formatAuctionCatalogueDateRange,
    getCatalogueCollectionPublicCopy,
    getCataloguePublicCopy,
    isStaggeredAuctionCatalogue,
    localiseAuctionCatalogueSchedule,
    matchAuctionCataloguePath,
    normaliseAuctionCataloguePath,
    normaliseCatalogueNumber,
    resolveLiveCatalogueShortcutHref,
    resolveUpcomingCatalogueShortcutHref,
    selectCurrentCatalogue,
    shouldHideAuctionPageFromCrawlers
};
